import { useSyncExternalStore } from 'react';

export const PAGE_SIZE_DEFAULT = 100;
export const PAGE_SIZE_MIN = 5;
export const PAGE_SIZE_MAX = 300;
export const PAGE_SIZE_PRESETS = [100, 150, 200, 250, 300];

const STORAGE_KEY = 'mog.tablePageSize';

const AUTO = { mode: 'auto' };
const DEFAULT_PREF = { mode: 'manual', size: PAGE_SIZE_DEFAULT };

let snapshot = DEFAULT_PREF;
const listeners = new Set();

export function clampPageSize(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return PAGE_SIZE_DEFAULT;
  return Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, n));
}

function readStoredRaw() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredRaw(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Ignore private-mode storage failures; the in-memory choice still applies this session.
  }
}

function readPreference() {
  const raw = readStoredRaw();
  if (raw == null || raw === '') {
    writeStoredRaw(String(PAGE_SIZE_DEFAULT));
    return DEFAULT_PREF;
  }
  if (raw === 'auto') return AUTO;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) {
    writeStoredRaw(String(PAGE_SIZE_DEFAULT));
    return DEFAULT_PREF;
  }
  const size = clampPageSize(n);
  if (String(size) !== raw) writeStoredRaw(String(size));
  return { mode: 'manual', size };
}

function samePreference(a, b) {
  return a.mode === b.mode && a.size === b.size;
}

function publish(next) {
  if (samePreference(snapshot, next)) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getTablePageSizePreference() {
  return snapshot;
}

export function setTablePageSizePreference(next) {
  const pref =
    next && next.mode === 'manual'
      ? { mode: 'manual', size: clampPageSize(next.size) }
      : AUTO;
  writeStoredRaw(pref.mode === 'auto' ? 'auto' : String(pref.size));
  publish(pref);
}

function subscribe(listener) {
  listeners.add(listener);
  const onStorage = (event) => {
    if (event.key != null && event.key !== STORAGE_KEY) return;
    publish(readPreference());
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

if (typeof window !== 'undefined') {
  snapshot = readPreference();
}

/** Shared rows-per-page choice for every paginated table. `auto` fits the viewport. */
export function useTablePageSizePreference() {
  return useSyncExternalStore(subscribe, getTablePageSizePreference, () => DEFAULT_PREF);
}
